import { useMemo } from 'react'
import type {
  AppRevenueSummary,
  KpiData,
  PeriodFilter,
  SettlementInfo,
  TimeSeriesPoint,
} from '@/types/dashboard'
import {
  createStudioReviewDataClient,
  type ImpactReviewData,
  type StudioReviewDataClient,
} from '@/services/dashboard-api'
import { useDashboardSlice, useReloadToken } from './dashboard-data-state'
import type { DashboardSlice } from './dashboard-data-state'

export interface RevenueData {
  kpis: DashboardSlice<KpiData[]>
  timeSeries: DashboardSlice<TimeSeriesPoint[]>
  appRevenue: DashboardSlice<AppRevenueSummary[]>
  settlement: DashboardSlice<SettlementInfo | null>
  isLoading: boolean
  reload: () => void
}

function projectSlice<T>(
  slice: DashboardSlice<ImpactReviewData>,
  select: (data: ImpactReviewData) => T,
): DashboardSlice<T> {
  return {
    ...slice,
    data: slice.data === null ? null : select(slice.data),
    evidenceRefs: [...slice.evidenceRefs],
  }
}

export function useRevenueData(
  period: PeriodFilter,
  injectedClient?: StudioReviewDataClient,
): RevenueData {
  const defaultClient = useMemo(() => createStudioReviewDataClient(), [])
  const client = injectedClient ?? defaultClient
  const [reloadToken, reload] = useReloadToken()
  const impact = useDashboardSlice(
    'impact',
    () => client.readImpact(period),
    [client, period],
    reloadToken,
  )

  return {
    kpis: projectSlice(impact, (data) => data.kpis),
    timeSeries: projectSlice(impact, (data) => data.timeSeries),
    appRevenue: projectSlice(impact, (data) => data.appRevenue),
    settlement: projectSlice(impact, (data) => data.settlement),
    isLoading: impact.status === 'loading',
    reload,
  }
}
